import { fetchy } from '@bridebook/web/lib/fetchy';
import { toMillis } from '@bridebook/toolbox/src/time-utils/time-utils';
import { useQuery } from '@tanstack/react-query';
import type { EventResponse as OpenDayEventResponse } from '@bridebook/web/pages/api/supplier/[id]/calendar/[calendarId]/events/index';

export type { OpenDayEventResponse };

const OPEN_DAYS_QUERY_CACHE_TIME = toMillis(2, 'min');

export const getAllOpenDaysQueryKey = (supplierId: string, calendarId?: string) => [
  'all-open-days',
  supplierId,
  calendarId,
];

/**
 * Fetches all upcoming open day events for a supplier's single openDay calendar.
 *
 * Each event includes `calendarId` so that if we need per-event arrival slots
 * in the future (e.g. staged appearances), we can fetch
 * GET /api/supplier/[id]/calendar/[calendarId]/available-slots for that specific calendar.
 */
export const useGetAllOpenDaysQuery = (
  supplierId: string,
  calendarId?: string,
  options?: { enabled?: boolean },
) => {
  const { data, error, isFetching } = useQuery(
    getAllOpenDaysQueryKey(supplierId, calendarId),
    async (): Promise<OpenDayEventResponse[]> => {
      if (!supplierId || !calendarId) return [];
      return fetchy().authenticated.get('/api/supplier/[id]/calendar/[calendarId]/events', {
        id: supplierId,
        calendarId,
      });
    },
    {
      enabled: !!supplierId && !!calendarId && options?.enabled !== false,
      refetchOnWindowFocus: false,
      cacheTime: OPEN_DAYS_QUERY_CACHE_TIME,
      staleTime: OPEN_DAYS_QUERY_CACHE_TIME,
    },
  );

  return {
    openDays: data || [],
    isLoading: isFetching,
    error,
  };
};
